import { AggregateValidationError } from '../public'
import { isValidator, validateShape } from './validateShape'
import { ShapeFor } from './shapeFor'
import { ValidatorOrShape } from '../validators/types'

/**
 * Type guard function that asserts an object matches a given shape.
 * @template T - The type of object being validated
 * @param obj - The object to validate
 * @throws {AggregateValidationError} If the object doesn't match the shape
 */
export interface AssertShape<T extends object> {
  (obj: unknown): asserts obj is T
}

/**
 * Creates a type guard function that asserts an object matches a given shape.
 * @template T - The type of object being validated
 * @param shape - The shape to validate against
 * @returns A type guard function that throws if validation fails
 */
export function createAssertShape<T extends object>(shape: ShapeFor<T>): AssertShape<T> {
  return (obj: unknown): asserts obj is T => {
    assertShape<T>(obj, shape)
  }
}

/**
 * Asserts that an object matches a given shape.
 * This is a type guard function that can be used to narrow the type of an object.
 * 
 * @template T - The type of object being validated
 * @param obj - The object to validate
 * @param shape - The shape to validate against
 * @throws {AggregateValidationError} If the object doesn't match the shape
 * 
 * @remarks
 * - If the shape is a validator, it will be used to validate the entire object
 * - If the shape is an object, it will be used to validate each property recursively
 * - All validation errors are collected and thrown as an AggregateValidationError
 */
export const assertShape: <T extends object>(obj: unknown, shape: ShapeFor<T>) => asserts obj is T = <T extends object>(obj: unknown, shape: ShapeFor<T>): asserts obj is T => {  
  if (isValidator(shape)) {
    const results = shape(obj)
    const success = results.every(r => r.valid)
    if (!success) {
      throw new AggregateValidationError(results.filter(r => !r.valid))
    }
    return
  }

  const { success, results } = validateShape(obj, shape as ValidatorOrShape)
  if (!success) {
    throw new AggregateValidationError(results)
  }
}